import React, { useContext, useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  FaArrowRight,
  FaIdCard,
  FaWallet,
  FaStore,
  FaBoxOpen,
  FaCheckCircle,
} from "react-icons/fa";

import { AppContext } from "../../App";
import { CDN_URL, getInlineLoader } from "../../Helpers";

export default function SampleProjects() {
  const appContext = useContext(AppContext) || {};
  const { getAllProducts, navTo } = appContext;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Helper to dynamically assign react icons based on product title
  const getCategoryIcon = (title = "") => {
    const lower = title.toLowerCase();
    if (lower.includes("biz") || lower.includes("store") || lower.includes("shop")) return <FaStore />;
    if (lower.includes("smart") || lower.includes("id") || lower.includes("school")) return <FaIdCard />;
    if (lower.includes("pay") || lower.includes("wallet") || lower.includes("finance")) return <FaWallet />;
    return <FaBoxOpen />;
  };

  useEffect(() => {
    let isMounted = true;

    const fetchProducts = async () => {
      try {
        if (typeof getAllProducts === "function") {
          const res = await getAllProducts();
          const rawData = Array.isArray(res) ? res : res?.data || [];

          // STRICT FILTER: Only include Enterprise category data
          const enterpriseProducts = rawData.filter((item) => {
            const cat = (item.category || item.categoryName || item.type || item.group || "").toLowerCase();
            const tags = Array.isArray(item.tags) ? item.tags.map((t) => t.toLowerCase()) : [];
            
            return (
              cat.includes("enterprise") ||
              tags.includes("enterprise") ||
              item.isEnterprise === true
            );
          });

          if (isMounted) {
            setProducts(enterpriseProducts);
          }
        }
      } catch (err) {
        console.error("Error fetching products from AppContext:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchProducts();

    try {
      AOS.init({
        duration: 1000,
        easing: "ease-out-expo",
        once: true,
        offset: 50,
      });
      AOS.refresh();
    } catch (err) {
      console.warn("AOS animation initialization failed:", err);
    }

    return () => {
      isMounted = false;
    };
  }, [getAllProducts]);

  function handleViewDetails(uuid) {
    if (typeof navTo === "function" && uuid) {
      navTo({
        item: "view",
        subItem: "product",
        extraItem: uuid,
      });
    }
  }

  if (loading) {
    return (
      <div className="container mSupportLoading py-5 text-center">
        {typeof getInlineLoader === "function" ? getInlineLoader() : <div>Loading Enterprise Solutions...</div>}
      </div>
    );
  }

  return (
    <section
      className="sample-projects position-relative overflow-hidden py-6"
      style={{
        background: "#fcfcfd",
        minHeight: "100vh",
      }}
    >
      {/* Background Pattern */}
      <div
        className="position-absolute top-0 start-0 w-100 h-100"
        style={{
          zIndex: 0,
          pointerEvents: "none",
          backgroundImage: `radial-gradient(#e1e1e1 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
          opacity: 0.5,
        }}
      ></div>

      <div
        className="container position-relative"
        style={{ zIndex: 1, maxWidth: "1300px" }}
      >
        {/* Header */}
        <div className="text-center mb-6 pt-4">
          <div
            className="d-inline-flex align-items-center mb-3 px-3 py-1 rounded-pill"
            style={{
              background: "rgba(18,18,28,0.05)",
              color: "#1a1a2e",
              fontSize: "0.75rem",
              fontWeight: "700",
              letterSpacing: "1.5px",
            }}
          >
            ENTERPRISE SOLUTIONS
          </div>
          <h2
            className="fw-bold mb-3"
            data-aos="fade-up"
            style={{
              fontSize: "clamp(2.2rem, 4vw, 3rem)",
              color: "#1a1a2e",
              letterSpacing: "-0.03em",
            }}
          >
            Our{" "}
            <span style={{ color: "var(--secondaryColor, #f4911e)" }}>
              Enterprise Products
            </span>
          </h2>
          <p
            className="text-muted mx-auto"
            data-aos="fade-up"
            data-aos-delay="100"
            style={{ maxWidth: "650px" }}
          >
            High-performance digital tools designed to automate complex
            institutional workflows and enterprise financial processes.
          </p>
        </div>

        {/* Project Grid */}
        {products.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <p>No Enterprise products available at the moment.</p>
          </div>
        ) : (
          <div className="row g-4">
            {products.map((product, idx) => {
              const title = product.title || product.name || "Enterprise Product";
              const subtitle = product.subtitle || product.category || "Enterprise Solution";
              const description = product.description || "No description available for this enterprise product.";
              
              // Dynamic CDN Image resolution matching OneProduct logic
              const coverPhotoPath = product.coverPhoto || product.cover_photo || product.image;
              const imageUrl = coverPhotoPath ? CDN_URL + coverPhotoPath : "";
              
              const accent = product.accent || product.color || "#f4911e";
              
              // Features normalization
              const rawFeatures = product.features || product.highlights || [];
              const features = rawFeatures.map((f) => 
                typeof f === "string" ? { label: f, icon: <FaCheckCircle /> } : f
              );

              // Stats normalization
              const stats = product.stats || [
                { value: "360°", label: "Visibility" },
                { value: "Enterprise", label: "Grade" },
              ];

              return (
                <div
                  key={product.uuid || product.id || idx}
                  className="col-xl-4 col-md-6"
                  data-aos="fade-up"
                  data-aos-delay={idx * 100}
                >
                  <div
                    className="project-card rounded-3 h-100 border-0 bg-white shadow-sm overflow-hidden d-flex flex-column"
                    style={{
                      transition: "all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)",
                    }}
                  >
                    {/* Visual Area */}
                    <div
                      className="position-relative"
                      style={{ height: "240px", overflow: "hidden" }}
                    >
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={title}
                          loading="lazy"
                          className="w-100 h-100 project-image"
                          style={{
                            objectFit: "cover",
                            transition: "transform 1.2s ease",
                          }}
                        />
                      ) : (
                        <div
                          className="w-100 h-100 bg-secondary opacity-25"
                        ></div>
                      )}

                      <div
                        className="position-absolute top-0 start-0 w-100 h-100"
                        style={{
                          background:
                            "linear-gradient(180deg, transparent 30%, rgba(0,0,0,0.7) 100%)",
                        }}
                      ></div>

                      <div className="position-absolute bottom-0 start-0 p-4 w-100">
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <h5 className="text-white fw-bold mb-0">
                              {title}
                            </h5>
                            <span className="text-white-50 small">{subtitle}</span>
                          </div>
                          <div
                            className="project-icon-box p-3 rounded-circle bg-white"
                            style={{
                              color: accent,
                              width: "50px",
                              height: "50px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {product.icon || getCategoryIcon(title)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Info Area */}
                    <div className="p-4 flex-grow-1 d-flex flex-column">
                      <p
                        className="text-muted mb-4"
                        style={{ fontSize: "0.9rem", lineHeight: 1.6 }}
                      >
                        {description}
                      </p>

                      <div className="row g-3 mb-auto">
                        {features.map((feature, fIdx) => (
                          <div key={fIdx} className="col-12">
                            <div className="d-flex align-items-center gap-3">
                              <span
                                className="p-2 rounded-2"
                                style={{
                                  background: `${accent}15`,
                                  color: accent,
                                  fontSize: "0.9rem",
                                }}
                              >
                                {feature.icon || <FaCheckCircle />}
                              </span>
                              <span className="small font-semi-bold text-muted">
                                {feature.label || feature}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="d-flex align-items-center justify-content-between pt-4 mt-4 border-top">
                        <div className="d-flex gap-3">
                          {stats.map((stat, sIdx) => (
                            <div key={sIdx}>
                              <div
                                className="fw-bold mb-0"
                                style={{ color: "#1a1a2e", fontSize: "0.9rem" }}
                              >
                                {stat.value}
                              </div>
                              <div
                                className="text-uppercase text-muted"
                                style={{
                                  fontSize: "0.6rem",
                                  fontWeight: "700",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                {stat.label}
                              </div>
                            </div>
                          ))}
                        </div>

                        <button
                          type="button"
                          onClick={() => handleViewDetails(product.uuid)}
                          className="btn-link-custom d-flex align-items-center gap-2 fw-bold text-uppercase border-0 bg-transparent p-0"
                          style={{
                            color: "var(--secondaryColor, #f4911e)",
                            fontSize: "0.75rem",
                            letterSpacing: "1px",
                            cursor: "pointer",
                          }}
                        >
                          View Details <FaArrowRight size={10} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <style>{`
        .project-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 30px 60px rgba(0,0,0,0.1) !important;
        }
        .project-card:hover .project-image {
          transform: scale(1.1);
        }
        .project-icon-box {
          transition: all 0.3s ease;
        }
        .project-card:hover .project-icon-box {
          background: #12121c !important;
          color: #fff !important;
        }
        .btn-link-custom {
          transition: all 0.3s ease;
        }
        .btn-link-custom:hover {
          gap: 10px !important;
          opacity: 0.8;
        }
        .tracking-widest { letter-spacing: 0.15em; }
        .py-6 { padding-top: 5rem; padding-bottom: 5rem; }
        .mb-6 { margin-bottom: 3.5rem; }
      `}</style>
    </section>
  );
}